// @flow
import React, { createRef } from 'react';
import Slider from 'react-slick';
import slick from 'slick-carousel/slick/slick.css';
import styles from './styles.scss';
import classNames from 'classnames/bind';
import AriaDescribedby from 'Components/AriaDescribedby';
import { v4 as uuidv4 } from 'uuid';

const cx = classNames.bind(styles);

function SliderArrow(props) {
  const { className, style, onClick } = props;
  return <button className={className} aria-hidden="true" onClick={onClick} />;
}

type Props = {
  children: Node,
  infiniteLoop?: boolean,
  selectedItem?: number,
  onChange?: (number) => void,
  autoPlay?: boolean,
  showIndicators?: boolean,
  showArrows?: boolean,
  showStatus?: boolean,
  transparentBackground?: boolean,
  stopOnHover?: boolean,
  interval?: number,
  transitionTime?: number,
  draggable?: boolean,
  className?: string,
  updateCurrentIndex?: (number) => void,
  wholeAreaIsClickable?: boolean,
  highlightedArrows?: boolean,
  postionTeaser?: boolean,
};

type State = {
  currentIndex: number,
  settings: object,
};

export default class Carousel extends React.Component<Props, State> {
  static defaultProps = {
    infiniteLoop: true,
    selectedItem: 0,
    autoPlay: false,
    showIndicators: false,
    showArrows: true,
    showStatus: true,
    stopOnHover: true,
    interval: 6000,
    transitionTime: 350,
    draggable: true,
    className: null,
    postionTeaser: false,
  };

  slider: Node = createRef(null);
  sliderWrapper = createRef([]);
  sliderIsInitialized = false;

  constructor(props: Props) {
    super(props);

    this.state = {
      currentIndex: props.selectedItem + 1 || 1,
      settings: {
        infinite: this.getSeetingsInfinite(),
        dots: props.showIndicators,
        arrows: props.showArrows,
        adaptiveHeight: false,
        autoplay: props.autoPlay,
        initialSlide: props.selectedItem,
        pauseOnHover: props.stopOnHover,
        autoplaySpeed: props.interval,
        speed: props.transitionTime,
        draggable: props.draggable,
        nextArrow: <SliderArrow />,
        prevArrow: <SliderArrow />,
      },
    };
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.updateCurrentIndex && this.state.currentIndex !== prevState.currentIndex) {
      this.props.updateCurrentIndex(this.state.currentIndex);
    }

    if (prevProps.children.length !== this.props.children.length) {
      this.updateSettings({ infinite: this.getSeetingsInfinite() });
    }

    if (!this.sliderIsInitialized && this.slider.current) {
      this.updateTabIndex(this.slider.current.innerSlider.state.currentSlide);
      this.sliderIsInitialized = true;
    }
  }

  updateSettings = (props) => {
    this.setState({ settings: { ...this.state.settings, ...props } });
  };

  getSeetingsInfinite = () => {
    return React.Children.count(this.props.children) > 1 ? this.props.infiniteLoop : false;
  };

  UNSAFE_componentWillReceiveProps = (nextProps: Props) => {
    if (this.slider.current && this.props.selectedItem !== nextProps.selectedItem) {
      this.slider.current.slickGoTo(nextProps.selectedItem);
    }
  };

  onAfterChange = (index: number) => {
    const elementsNotAllowedToFokus = this.getElements(index, false);
    const activeElementIsNotVisible = [...elementsNotAllowedToFokus].some(
      (item) => item === document.activeElement
    );
    if (activeElementIsNotVisible) {
      const elementsAllowedToFokus = this.getElements(index, true);
      elementsAllowedToFokus[0].focus();
    }
  };

  getElements = (activeSlideIndex, allowedToFocus) => {
    const focusableElements = ['a', 'button'];
    const selector = focusableElements
      .map((focusable) => {
        if (allowedToFocus) {
          return `.slick-slide[data-index="${activeSlideIndex}"] ${focusable}`;
        }

        return `.slick-slide:not([data-index="${activeSlideIndex}"]) ${focusable}`;
      })
      .join(',');
    return this.sliderWrapper.current.querySelectorAll(selector);
  };

  updateTabIndex = (activeSlideIndex) => {
    const elementsNotAllowedToFokus = this.getElements(activeSlideIndex, false);
    const elementsAllowedToFokus = this.getElements(activeSlideIndex, true);

    [...elementsNotAllowedToFokus].forEach((element) => {
      element.setAttribute('tabindex', '-1');
      element.setAttribute('aria-hidden', 'true');
    });

    [...elementsAllowedToFokus].forEach((element) => {
      element.removeAttribute('tabindex');
      element.removeAttribute('aria-hidden');
    });
  };

  onBeforeChange = (oldIndex: number, newIndex: number) => {
    this.setState({ currentIndex: newIndex + 1 });
    this.props.onChange && this.props.onChange(newIndex);

    this.updateTabIndex(newIndex);
  };

  next = () => {
    this.slider.current.slickNext();
  };

  prev = () => {
    this.slider.current.slickPrev();
  };

  handleClick = (event) => {
    if (event.clientX < window.innerWidth / 2) {
      this.prev();
    } else {
      this.next();
    }
  };

  handleFocus = (event) => {
    this.updateSettings({ autoplay: false });
  };

  handleBlur = (event) => {
    this.updateSettings({ autoplay: this.props.autoPlay });
  };

  handleKeyboard = (event) => {
    if (event.code === 'ArrowDown') {
      event.preventDefault();
      this.next();
    } else if (event.code === 'ArrowUp') {
      event.preventDefault();
      this.prev();
    }
  };

  render() {
    const {
      children,
      showStatus,
      transparentBackground,
      className,
      wholeAreaIsClickable,
      highlightedArrows,
      postionTeaser,
      infiniteLoop,
    } = this.props;

    const { settings } = this.state;

    const ariaDescribedbyId = `aria-describedby-${uuidv4()}`;

    return (
      <div
        className={cx(
          [styles.root],
          className,
          highlightedArrows && styles.highlightedArrows,
          postionTeaser && styles.teaserStageContent,
          transparentBackground && styles.transparentBackground
        )}
        role="region"
        aria-roledescription="Gallerie"
        aria-describedby={ariaDescribedbyId}
        aria-live={infiniteLoop ? 'off' : 'polite'}
        onClick={wholeAreaIsClickable && this.handleClick}
        onFocus={this.handleFocus}
        onBlur={this.handleBlur}
        onKeyDown={this.handleKeyboard}
      >
        <div ref={this.sliderWrapper}>
          <Slider
            {...settings}
            ref={this.slider}
            beforeChange={this.onBeforeChange}
            afterChange={this.onAfterChange}
          >
            {children}
          </Slider>
        </div>
        {showStatus && (
          <div className={styles.status}>
            <div className={styles.statusText}>
              {this.state.currentIndex}/{React.Children.count(children)}
            </div>
          </div>
        )}
        <AriaDescribedby
          id={ariaDescribedbyId}
          textHover="Wische für weitere Slide"
          textFocus="Nutze die Pfeiltasten für weitere Slides"
          textHoverAndFocus="Wische oder nutze die Pfeiltasten für weitere Slides"
        />
      </div>
    );
  }
}
